import { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useMotionTemplate,
  useSpring,
  useTransform,
  animate,
} from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Flashlight } from "lucide-react";
import Nav from "@/components/Nav";
import Letter from "@/components/Letter";
import Marquee from "@/components/Marquee";
import { Tag, BrushHighlight } from "@/components/sections/shared";
import TiltedCard from "@/components/TiltedCard/TiltedCard";

const EASE = [0.16, 1, 0.3, 1];

const headlineLines = [
  { text: "Think your claim was denied,", gold: false },
  { text: "delayed, or lowballed?", gold: false },
];

const HeadlineLine = ({ text, gold, index }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: EASE, delay: 0.35 + index * 0.14 }}
      className={`font-display font-semibold whitespace-nowrap leading-[1.08] pb-[0.06em] text-3xl sm:text-5xl lg:text-[3.2rem] xl:text-5xl ${
        gold ? "text-[#9D4EDD]" : "text-white"
      }`}
    >
      {text}
    </motion.div>
  </div>
);

export const Hero = () => {
  const navigate = useNavigate();
  const letterRef = useRef(null);
  const sweepRefs = useRef([]);
  const sweptByUser = useRef(false);
  const [hovering, setHovering] = useState(false);

  const lx = useMotionValue(-400);
  const ly = useMotionValue(-400);
  const cx = useMotionValue(-400);
  const cy = useMotionValue(-400);

  const ringX = useSpring(cx, { stiffness: 500, damping: 40 });
  const ringY = useSpring(cy, { stiffness: 500, damping: 40 });

  const mask = useMotionTemplate`radial-gradient(240px circle at ${lx}px ${ly}px, black 30%, rgba(0,0,0,0.4) 62%, transparent 80%)`;
  const beam = useMotionTemplate`radial-gradient(650px circle at ${cx}px ${cy}px, rgba(255,209,102,0.07), transparent 70%)`;

  const jackX = useTransform(cx, (v) => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1920;
    return (v / w - 0.5) * -16;
  });

  const stopSweep = () => {
    if (!sweptByUser.current) {
      sweptByUser.current = true;
      sweepRefs.current.forEach((c) => c && c.stop());
    }
  };

  const setFromPoint = (clientX, clientY) => {
    cx.set(clientX);
    cy.set(clientY);
    const r = letterRef.current?.getBoundingClientRect();
    if (r) {
      lx.set(clientX - r.left);
      ly.set(clientY - r.top);
    }
  };

  const handleMouseMove = (e) => {
    stopSweep();
    setFromPoint(e.clientX, e.clientY);
  };

  const handleTouchMove = (e) => {
    stopSweep();
    const t = e.touches[0];
    if (t) setFromPoint(t.clientX, t.clientY);
  };

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timer = setTimeout(() => {
      const r = letterRef.current?.getBoundingClientRect();
      if (!r) return;
      if (reduced) {
        lx.set(r.width / 2);
        ly.set(r.height / 2);
        return;
      }
      const coarse = window.matchMedia("(pointer: coarse)").matches;
      const opts = coarse
        ? { duration: 5.5, ease: "easeInOut", repeat: Infinity, repeatType: "mirror" }
        : { duration: 3.6, ease: "easeInOut" };
      sweepRefs.current = [
        animate(lx, coarse ? [-160, r.width * 0.55, r.width * 0.3, r.width + 160] : [-160, r.width * 0.85, r.width * 0.52], opts),
        animate(ly, coarse ? [r.height * 0.12, r.height * 0.4, r.height * 0.65, r.height * 0.9] : [r.height * 0.15, r.height * 0.55, r.height * 0.4], opts),
      ];
    }, 1500);
    return () => {
      clearTimeout(timer);
      sweepRefs.current.forEach((c) => c && c.stop());
    };
  }, [lx, ly]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className="hero-no-cursor relative flex flex-col min-h-screen overflow-hidden bg-[#0A0A0C]"
    >
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(138,43,226,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(138,43,226,0.4) 1px, transparent 1px)",
          backgroundSize: "76px 76px",
        }}
      />
      <div className="absolute -top-48 -right-48 w-[880px] h-[880px] rounded-full border border-[#9D4EDD]/[0.08]" />
      <div className="absolute top-24 -right-24 w-[560px] h-[560px] rounded-full border border-[#FFD166]/[0.08]" />
      <div
        className="absolute bottom-0 right-0 w-[700px] h-[700px] pointer-events-none"
        style={{ background: "radial-gradient(circle at 75% 85%, rgba(138,43,226,0.16), transparent 60%)" }}
      />
      <motion.div className="absolute inset-0 pointer-events-none" style={{ background: beam }} />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(120% 120% at 50% 45%, transparent 55%, rgba(0,0,0,0.65) 100%)" }}
      />

      <Nav />

      <div className="relative z-10 flex-1 w-full max-w-[1440px] mx-auto px-6 lg:px-14 pt-28 lg:pt-32 pb-10 grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        <div className="lg:col-span-6 max-w-xl">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.25 }}
          >
            <Tag testId="hero-eyebrow">The Rip-Off Detector</Tag>
          </motion.div>

          <h1 data-testid="hero-headline">
            {headlineLines.map((l, i) => (
              <HeadlineLine key={i} text={l.text} gold={l.gold} index={i} />
            ))}
          </h1>

          <motion.p
            data-testid="hero-tagline"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
            className="mt-3 font-serif-jack italic text-2xl text-[#FFD166]/90"
          >
            Run it through the Rip-Off Detector.
          </motion.p>

          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.95 }}
            className="mt-6 text-base text-[#A1A1AA] leading-relaxed font-light"
          >
            The Rip-Off Detector helps identify possible red flags in your insurance claim. You
            can share what happened, paste confusing claim language, or describe the offer you
            received. Jack will help you understand what may be going on and what questions you
            should be asking next.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => navigate("/rip-off-detector")}
              className="group flex items-center gap-2 bg-[#FFD166] text-black font-display text-sm tracking-[0.18em] px-7 py-4 shadow-[5px_5px_0_#8A2BE2] hover:bg-[#F5CB5C] hover:shadow-[3px_3px_0_#8A2BE2] hover:translate-x-[2px] hover:translate-y-[2px] transition-[background-color,box-shadow,transform] duration-200"
            >
              TAKE THE <BrushHighlight>FREE</BrushHighlight> RIP-OFF DETECTOR
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1.5"
              />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => window.AIWidget?.ask()}
              className="font-display text-sm tracking-[0.18em] text-[#9D4EDD] border border-[#8A2BE2]/60 px-7 py-4 hover:bg-[#8A2BE2]/10 hover:border-[#9D4EDD] transition-colors duration-300"
            >
              ASK JACK
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-8 text-[11px] text-[#71717A] font-light max-w-md"
          >
            The Rip-Off Detector provides educational guidance and does not provide legal advice,
            public adjusting services, or guaranteed claim outcomes.
          </motion.p>
        </div>

        <div className="lg:col-span-6 relative flex justify-center lg:justify-start">
          <motion.div
            initial={{ opacity: 0, y: 50, rotate: 6 }}
            animate={{ opacity: 1, y: 0, rotate: 2 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.6 }}
            className="relative w-full max-w-[490px]"
          >
            <div className="flex items-center gap-2.5 mb-3 ml-6 -rotate-5">
              <Flashlight size={14} className="text-[#FFD166]" />
              <span
                data-testid="hero-letter-instruction"
                className="font-mono-file text-[10px] tracking-[0.25em] text-[#FFD166]/90"
              >
                <span className="hidden md:inline">MOVE YOUR LIGHT ACROSS THE LETTER</span>
                <span className="md:hidden" aria-hidden="true">DRAG THE LIGHT TO DECODE</span>
              </span>
            </div>

            <div
              ref={letterRef}
              data-testid="hero-letter"
              className="relative shadow-[0_40px_90px_-20px_rgba(0,0,0,0.7)]"
            >
              <Letter decoded={false} />
              <motion.div
                data-testid="hero-letter-truth-layer"
                className="absolute inset-0"
                style={{ WebkitMaskImage: mask, maskImage: mask }}
              >
                <Letter decoded={true} />
              </motion.div>
              <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-[#FFD166]/50" />
              <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-[#FFD166]/50" />
            </div>

            <div className="absolute -bottom-5 font-mono-file text-[9px] tracking-[0.25em] text-[#9D4EDD]/50">
              EXHIBIT A · CARRIER TRANSMISSION
            </div>
          </motion.div>

          <motion.div
            data-testid="hero-jack-portrait"
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3, ease: EASE, delay: 1.0 }}
            style={{ x: jackX }}
            onClick={() => window.AIWidget?.ask()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && window.AIWidget?.ask()}
            aria-label="Ask Jack"
            className="absolute right-0 -bottom-10 w-[180px] sm:w-[220px] lg:w-[260px] xl:w-[290px] z-20 cursor-pointer"
          >
            <TiltedCard
              imageSrc="/jack.png"
              altText="Jack D. Hapsburg"
              testId="hero-jack-image"
              containerHeight="clamp(250px, 23vw + 163px, 400px)"
              containerWidth="100%"
              imageHeight="clamp(250px, 23vw + 163px, 400px)"
              imageWidth="100%"
              scaleOnHover={1.05}
              rotateAmplitude={10}
              showMobileWarning={false}
              showTooltip={false}
              className="tilted-card-figure--square"
              innerClassName="rounded-t-full overflow-hidden border-2 border-[#FFD166]/60 shadow-[0_30px_80px_-10px_rgba(0,0,0,0.8)] after:absolute after:inset-0 after:content-[''] after:pointer-events-none after:bg-[linear-gradient(to_top,rgba(0,0,0,0.6),transparent_40%)]"
              imgClassName="object-top"
            />
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 w-[88%] bg-black/85 backdrop-blur-sm border border-[#FFD166]/40 px-3 py-2.5 sm:px-4 sm:py-3">
              <p className="font-serif-jack italic text-[10px] sm:text-[13px] text-[#FFD166]/90 leading-snug">
                "Don't tell me the whole story yet. Show me where it stopped making sense."
              </p>
              <p className="mt-1.5 font-mono-file text-[7px] sm:text-[9px] tracking-[0.2em] sm:tracking-[0.25em] text-[#FFD166]">
                JACK D. HAPSBURG
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <Marquee />

      <motion.div
        className="pointer-events-none fixed top-0 left-0 z-[70] hidden md:block"
        style={{ x: ringX, y: ringY }}
        animate={{ opacity: hovering ? 1 : 0 }}
        transition={{ duration: 0.25 }}
      >
        <div className="-translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full border border-[#FFD166]/80 flex items-center justify-center bg-[#FFD166]/[0.06]">
          <div className="w-1.5 h-1.5 rounded-full bg-[#FFD166]" />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
